import { Injectable } from '@angular/core';

/**
 * PermissionService — small frontend gate for write actions.
 *
 * The authoritative RBAC check lives on the backend (accessCtrl.vldInsert /
 * vldUpdate / vldDelete reading usr_rle_objct_rel_t). This helper mirrors the
 * decision on the client purely for UX: read-only users (e.g. the audit user)
 * see the save/update/delete buttons disabled with a clear "no permission"
 * tooltip instead of clicking and being slapped with ACCESS_DENIED.
 *
 * If localStorage `wto_w_user` is missing or malformed, we assume the user
 * IS allowed — same fail-open posture as the backend (the backend then
 * enforces). The intent here is UX, not security.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionService {

  /** Cached user record from localStorage to avoid repeated JSON parsing. */
  private user: any | null = null;

  constructor() { }

  private loadUser(): any | null {
    if (this.user !== null) return this.user;
    try {
      const raw = localStorage.getItem('wto_w_user');
      this.user = raw ? JSON.parse(raw) : null;
    } catch {
      this.user = null;
    }
    return this.user;
  }

  /** Forget cached user (call this on logout or role change). */
  invalidate(): void {
    this.user = null;
  }

  /**
   * Is the current user explicitly marked read-only?
   * The system stores write permissions in usr_rle_objct_rel_t, but for UX
   * we trust two markers carried on the session user object:
   *  - `read_only_in === 1` — flag we can set on audit accounts
   *  - `assignedProfiles.usr_rle` matching a known read-only role id
   * Modify the role-id allow-list below to match your read-only roles.
   */
  isReadOnly(): boolean {
    const u = this.loadUser();
    if (!u) return false;
    if (u.read_only_in === 1) return true;
    // Known read-only role ids (audituser is rle_id 4000001 per audit role).
    // Add more as your RBAC schema grows.
    const READ_ONLY_RLE_IDS = new Set<number>([4000001]);
    const rleId = u.assignedProfiles && u.assignedProfiles.usr_rle;
    if (typeof rleId === 'number' && READ_ONLY_RLE_IDS.has(rleId)) {
      return true;
    }
    if (typeof rleId === 'string' && READ_ONLY_RLE_IDS.has(parseInt(rleId, 10))) {
      return true;
    }
    return false;
  }

  /** True when the user is allowed to perform any write (insert/update/delete). */
  canWrite(): boolean {
    return !this.isReadOnly();
  }

  /** Convenience for templates that want to gate a Save / Insert button. */
  canCreate(): boolean {
    return this.canWrite();
  }

  /** Convenience for templates that want to gate an Update button. */
  canUpdate(): boolean {
    return this.canWrite();
  }

  /** Convenience for templates that want to gate a Delete button. */
  canDelete(): boolean {
    return this.canWrite();
  }
}
